export interface BlogItem {
    id: string;
    tag: string;
    title: string;
    description: string;
    buttonText: string;
    buttonHref: string;
    mainImage?: string;
    mainImageFocalPoint?: string;
}

export interface Category {
    id: string;
    name: string;
}

export interface BlogCardListOptions {
    title?: string;
    blogs: BlogItem[];
    categories?: Category[];
    featuredBlogId?: string;
    featuredBlogIndex?: number;
}

const ITEMS_PER_PAGE = 7;
const DEFAULT_CATEGORIES = {
    allCategories: [
        "All categories",     // EN
        "Alle Kategorien",    // DE
        "Todas las categor�as", // ES
        "Toutes les cat�gories" // FR
    ],
    allPlaces: [
        "All places",        // EN
        "Alle Orte",         // DE
        "Todos los lugares", // ES
        "Tous les lieux"     // FR
    ]
};

class BlogCardList {
    private element: HTMLElement;
    private showMoreContainer: HTMLElement;
    private gridBlogs: NodeListOf<HTMLElement>;

    constructor(element: HTMLElement) {
        this.element = element;
        this.showMoreContainer = element.querySelector('.blog-card-list__show-more') as HTMLElement;
        this.gridBlogs = element.querySelectorAll('.blog-card-list__grid-item');

        if (this.showMoreContainer) {
            if (this.gridBlogs.length <= ITEMS_PER_PAGE) {
                this.showMoreContainer.classList.add('hidden');
            } else {
                this.showMoreContainer.classList.remove('hidden');
            }
        }

        this.init();
    }

    private init = () => {
        const filterButtons = this.element.querySelectorAll('.blog-card-list__category-filters .pill-button');
        const featuredCardEl = this.element.querySelector('.blog-card-list__featured-card');
        const gridItems = this.element.querySelectorAll('.blog-card-list__grid-item');
        const noResultsEl = this.element.querySelector('.blog-card-list__no-results') as HTMLElement;
        const showMoreContainer = this.element.querySelector('.blog-card-list__show-more') as HTMLElement;
        const showMoreButton = this.showMoreContainer?.querySelector('.show-more-button');

        filterButtons.forEach(button => {
            const category = button.getAttribute("data-category") || "All categories";
            if (DEFAULT_CATEGORIES.allCategories.includes(category) ||
                DEFAULT_CATEGORIES.allPlaces.includes(category)) {
                button.classList.add("highlight");
            }

            button.addEventListener('click', (e) => {

                const clickedButton = e.currentTarget as HTMLElement;
                const selectedCategory = clickedButton.getAttribute('data-category');

                filterButtons.forEach(btn => {
                    if (btn === clickedButton) {
                        btn.classList.add('highlight');
                    } else {
                        btn.classList.remove('highlight');
                    }
                });

                let visibleCount = 0;
                let displayedCount = 0;

                if (DEFAULT_CATEGORIES.allCategories.includes(category) ||
                    DEFAULT_CATEGORIES.allPlaces.includes(category)) {
                    if (featuredCardEl) {
                        featuredCardEl.classList.remove('hidden');
                        visibleCount++;
                    }

                    gridItems.forEach((item, index) => {
                        item.classList.remove('hidden');

                        if (index >= ITEMS_PER_PAGE) {
                            item.classList.add('hidden');
                        } else {
                            displayedCount++;
                        }

                        visibleCount++;
                    });

                    if (showMoreContainer && gridItems.length > ITEMS_PER_PAGE) {
                        showMoreContainer.classList.remove('hidden');
                    }
                } else {
                    if (featuredCardEl) {
                        const featuredTag = featuredCardEl.getAttribute('data-tag') || '';
                        const featuredCategory = featuredTag.includes('|') ?
                            featuredTag.split('|')[0].trim() : featuredTag;

                        if (featuredCategory === selectedCategory) {
                            featuredCardEl.classList.remove('hidden');
                            visibleCount++;
                        } else {
                            featuredCardEl.classList.add('hidden');
                        }
                    }

                    const categoryItems: HTMLElement[] = [];

                    gridItems.forEach(item => {
                        const itemTag = item.getAttribute('data-tag') || '';
                        const itemCategory = itemTag.includes('|') ?
                            itemTag.split('|')[0].trim() : itemTag;

                        if (itemCategory === selectedCategory) {
                            categoryItems.push(item as HTMLElement);
                            visibleCount++;

                            item.classList.add('hidden');
                        } else {
                            item.classList.add('hidden');
                        }
                    });

                    categoryItems.forEach((item, index) => {
                        if (index < ITEMS_PER_PAGE) {
                            item.classList.remove('hidden');
                            displayedCount++;
                        }
                    });

                    if (showMoreContainer) {
                        if (categoryItems.length > ITEMS_PER_PAGE) {
                            showMoreContainer.classList.remove('hidden');
                        } else {
                            showMoreContainer.classList.add('hidden');
                        }
                    }
                }

                if (visibleCount === 0 && noResultsEl) {
                    noResultsEl.classList.add('visible');
                    if (showMoreContainer) showMoreContainer.classList.add('hidden');
                } else if (noResultsEl) {
                    noResultsEl.classList.remove('visible');
                }

                if (showMoreButton) {
                    showMoreButton.setAttribute('data-category', selectedCategory || 'All categories');
                    showMoreButton.setAttribute('data-page', '1');
                }
            });
        });

        if (showMoreButton && this.gridBlogs.length > ITEMS_PER_PAGE) {
            showMoreButton.addEventListener('click', () => {
                const currentPage = parseInt(showMoreButton.getAttribute('data-page') || '1', 10);
                const currentCategory = showMoreButton.getAttribute('data-category') || 'All categories';
                const startIndex = currentPage * ITEMS_PER_PAGE;
                const endIndex = startIndex + ITEMS_PER_PAGE;
                let itemsInCategory = 0;
                let shownThisPage = 0;

                gridItems.forEach((item) => {
                    if (DEFAULT_CATEGORIES.allCategories.includes(currentCategory) ||
                        DEFAULT_CATEGORIES.allPlaces.includes(currentCategory)) {
                        const index = parseInt(item.getAttribute('data-index') || '0', 10);
                        if (index >= startIndex && index < endIndex) {
                            item.classList.remove('hidden');
                            shownThisPage++;
                        }
                        itemsInCategory++;
                    } else {
                        const itemTag = item.getAttribute('data-tag') || '';
                        const itemCategory = itemTag.includes('|') ?
                            itemTag.split('|')[1].trim() : itemTag;

                        if (itemCategory === currentCategory) {
                            itemsInCategory++;

                            if (itemsInCategory > startIndex && itemsInCategory <= startIndex + ITEMS_PER_PAGE) {
                                item.classList.remove('hidden');
                                shownThisPage++;
                            }
                        }
                    }
                });

                showMoreButton.setAttribute('data-page', (currentPage + 1).toString());

                if (DEFAULT_CATEGORIES.allCategories.includes(currentCategory)
                    || DEFAULT_CATEGORIES.allPlaces.includes(currentCategory)
                    && (startIndex + ITEMS_PER_PAGE) >= gridItems.length) {
                    showMoreContainer.classList.add('hidden');
                } else if (shownThisPage < ITEMS_PER_PAGE || itemsInCategory <= endIndex) {
                    showMoreContainer.classList.add('hidden');
                }
            });
        }
    };
};

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.blog-card-list').forEach(element => {
        new BlogCardList(element as HTMLElement);
    });
});